import classNames from 'classnames'
import { Typography } from 'components/ui'
import { formatCurrency } from '~utils'
import { formatDate } from '~utils/time-new'
import Template from '../common/template'

import styles from './CurrentBid.module.scss'

const { Text, Paragraph } = Typography

enum bidColor {
  initial = 'white',
  highest = 'green',
  outbid = 'red',
}

interface CurrentBidProps {
  bidValue: number
  nextMinimalBidValue: number
  startsAt?: Date
  endsAt?: Date
  bidState?:
    | 'initial'
    | 'highest'
    | 'outbid'
    | 'ended'
    | 'won'
    | 'paid'
    | 'will-start'
    | 'ended-for-highest-bidder'
}

const CurrentBid: React.FC<CurrentBidProps> = ({
  bidValue,
  nextMinimalBidValue,
  bidState,
  startsAt,
  endsAt,
}) => {
  const activeBidInfo = bidState === 'initial' || bidState === 'highest' || bidState === 'outbid'

  let title = 'CURRENT BID'

  if (bidState === 'ended' || bidState === 'won' || bidState === 'ended-for-highest-bidder') {
    title = 'WINNING BID'
  }
  if (bidState === 'will-start') {
    title = 'STARTING BID'
  }

  return (
    <Template iconName="dollar" title={title}>
      <span className={classNames(styles.number, styles[bidColor[bidState] || bidColor.initial])}>
        {formatCurrency(bidValue, 0)}
      </span>

      {bidState === 'will-start' && (
        <Paragraph color="light-gray">
          Bidding starts <Text bold>{formatDate(startsAt)}</Text> venue local time. Ends{' '}
          <Text bold>{formatDate(endsAt)}</Text> venue local time.
        </Paragraph>
      )}

      {activeBidInfo && (
        <Paragraph color="light-gray">
          {bidState === 'highest' && (
            <Text color={bidColor.initial} bold>
              You are the highest bidder.&nbsp;
            </Text>
          )}
          {bidState === 'outbid' && (
            <Text color={bidColor.outbid} bold>
              Someone outbid you.&nbsp;
            </Text>
          )}
          The next minimum bid is{' '}
          <Text color={bidColor.initial} bold>
            {formatCurrency(nextMinimalBidValue, 0)}
          </Text>
          . If you are the winner, you will not be charged until the auction is over.
        </Paragraph>
      )}

      {(bidState === 'won' || bidState === 'paid') && (
        <Paragraph>
          <Text color={bidColor.highest} bold>
            Congratulations! You won this auction.&nbsp;
          </Text>
          You will be contacted with further details.
        </Paragraph>
      )}
      {bidState === 'ended-for-highest-bidder' && (
        <Paragraph>You were the highest bidder! We will be in touch.</Paragraph>
      )}
      {bidState === 'ended' && <Paragraph>You were outbid. Better luck next time!</Paragraph>}
    </Template>
  )
}

interface BidStatusType {
  isUserHighestBidder?: boolean
  isUserOutbid?: boolean
  userWon?: boolean
  userPaid?: boolean
  isEnded?: boolean
  willStart?: boolean
}

export const returnBidStatus = ({
  isUserHighestBidder,
  isUserOutbid,
  userWon,
  willStart,
  userPaid,
  isEnded,
}: BidStatusType) => {
  if (willStart) return 'will-start'
  if (userPaid) return 'paid'
  if (userWon) return 'won'
  if (isEnded && isUserHighestBidder) return 'ended-for-highest-bidder'
  if (isEnded) return 'ended'
  if (isUserOutbid) return 'outbid'
  if (isUserHighestBidder) return 'highest'
  return 'initial'
}

export default CurrentBid
